import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { initialState } from './app.state';
import { AlertService, Collections, PROJECT_MESSAGES, ThemeMode, User } from '@ffx/common';
import { inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { collection, collectionData, doc, Firestore, query, setDoc, updateDoc, where } from '@angular/fire/firestore';

export const ApplicationStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withMethods((store) => {
        const fireStore = inject(Firestore);
        const alertService = inject(AlertService);

        return {
            clearStore(): void {
                patchState(store, {
                    ...initialState
                });
            },
            async setUserAndGetTheme(user: User): Promise<void> {
                // Patch state early in case theme fetch fails
                patchState(store, { user });
                const theme = store.user.theme();
                if (theme !== 'light' && theme !== 'dark') {
                    try {
                        const userCollection = collection(fireStore, Collections.users);
                        const getUserByUserIdQuery = query(userCollection, where('id', '==', user.uid));
                        const userFromDb = await firstValueFrom(
                            collectionData(getUserByUserIdQuery, { idField: 'id' })
                        );
                        patchState(store, {
                            user: {
                                uid: user.uid,
                                email: user.email,
                                theme: userFromDb[0]['theme'] || 'light'
                            }
                        });
                    } catch {
                        // Don't want to bother the user with an error alert here.
                        console.error("Error getting user's theme.");
                    }
                }
            },
            async signupUser(user: User): Promise<void> {
                try {
                    const docRef = doc(fireStore, `user/${user.uid}`);
                    await setDoc(docRef, { id: user.uid, email: user.email });
                    patchState(store, { user: user });
                } catch {
                    alertService.showNegativeAlert('Unable to add user. Please try again later.');
                }
            },
            async setUserTheme(theme: ThemeMode): Promise<void> {
                try {
                    const userUid = store.user.uid();
                    const userEmail = store.user.email();
                    const userDocRef = doc(fireStore, `${Collections.users}/${userUid}`);

                    await updateDoc(userDocRef, { theme });
                    patchState(store, {
                        user: {
                            email: userEmail,
                            uid: userUid,
                            theme
                        }
                    });
                } catch {
                    alertService.showNegativeAlert(PROJECT_MESSAGES.SET_USER_THEME);
                }
            }
        };
    })
);
